import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { GablePendingProposal } from '../../../../services/gable/gable.service';

export type ApprovalDialogResult = 'approve' | 'reject' | null;

/**
 * Dialog for confirming or rejecting an AI-proposed write.
 *
 * Renders the proposal summary + a JSON preview of the payload + three
 * buttons: Approve, Reject, Cancel. Emits `'approve' | 'reject' | null` on
 * `closed` so the parent page can drive the actual HTTP commit.
 */
@Component({
    selector: 'app-gable-approval-dialog',
    templateUrl: './approval-dialog.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CommonModule],
})
export class ApprovalDialogComponent {
    @Input() open = false;
    @Input() proposal!: GablePendingProposal;
    @Output() readonly closed = new EventEmitter<ApprovalDialogResult>();

    get payloadJson(): string {
        try {
            return JSON.stringify(this.proposal.payload, null, 2);
        } catch {
            return '(unable to serialize payload)';
        }
    }

    get payloadMarkdown(): string {
        return '```json\n' + this.payloadJson + '\n```';
    }

    get operationColor(): string {
        const op = String(this.proposal?.operation ?? '');
        return op === 'delete' ? 'red' : op === 'create' ? 'green' : 'orange';
    }

    onApprove(): void {
        this.closed.emit('approve');
    }

    onReject(): void {
        this.closed.emit('reject');
    }

    onCancel(): void {
        this.closed.emit(null);
    }
}
